import {useParams, Link, useLocation, useNavigationType, useSearchParams} from 'react-router-dom';
import { useLeaguePrefix, buildTeamLink, buildPublicTeamLink, buildMatchLink } from '../utils/leagueLinks';
import { useCurrentTeamId } from '../hooks/useCurrentTeamId';
import React, { useEffect, useState } from "react";
import { numToPrice, resolvePlayerAge } from '../components/utils.js';

import {PlayerStats} from "../components/player-page/PlayerStats";
import {PlayerChart} from "../components/player-page/PlayerChart";
import {TransferBlock} from "../components/player-page/TransferBlock";
import {TeamDropdown} from "../components/common/Dropdown.jsx";
import {Popup} from "../components/common/Popup";
import { Spinner } from '../components/common/Spinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { BackButton } from '../components/common/BackButton';
import api from '../services/api';
import TransferService from '../services/transferService';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { BID_STEP, computeNextMinimumBid } from '../utils/bidValidation.js';
import { isActiveTransferBidForPlayer } from '../utils/transferBidStatus.js';
import { useQueryClient } from '@tanstack/react-query';

import { useGetPlayerByWyId, useGetPlayerAttributes, useGetPlayerTransferHistory, useGetPlayerSeasonStats } from '../api/playersApi';
import { useGetCurrentSeason } from '../api/seasonsApi';

import { useActiveLeague } from '../hooks/useActiveLeague';
import seasons from "../json-data/seasons.json"

import Flag from "../components/common/Flag";
import TeamService from '../services/teamService';
import TutorialTooltip from "../components/common/TutorialTooltip";
import { tierOf } from '../components/cards/cardUtils.js';

const developmentOrder = ['sh_ab', 'ps_ab', 'tk_ab', 'st_ab'];

function PlayerDevelopmentPanel({ development }) {
    const progress = development?.progress || {};
    const recentChanges = (development?.recentEvents || [])
        .flatMap(event => event?.changes || [])
        .slice(-3)
        .reverse();

    if (!development || Object.keys(progress).length === 0) {
        return null;
    }

    return (
        <div className="development-block">
            <div className="title-block">
                <h4>Development</h4>
            </div>

            <div className="development-list">
                {developmentOrder.map((key) => {
                    const item = progress[key];
                    if (!item) return null;

                    const percent = Math.max(0, Math.min(100, item.percent || 0));
                    const progressText = item.nextUpgradeCost
                        ? `${item.current} / ${item.nextUpgradeCost}`
                        : `${item.current}`;

                    return (
                        <div className="development-row" key={key}>
                            <div className="development-row-head">
                                <h5>{item.label}</h5>
                                <span>{progressText}</span>
                            </div>
                            <div className="development-track" aria-hidden="true">
                                <span style={{ width: `${percent}%` }} />
                            </div>
                            {item.targetAttribute && (
                                <p>Next upgrade: {item.targetAttribute} +1</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {recentChanges.length > 0 && (
                <div className="development-changes">
                    <h5>Recent changes</h5>
                    {recentChanges.map((change, index) => (
                        <span key={`${change.attribute}-${index}`} className={change.direction}>
                            {change.attribute} {change.direction === 'upgrade' ? '+1' : '-1'}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function PlayerPage() {
    const { wyId, slug: routeSlug } = useParams();
    // Active league & current season from context
    const { activeLeague } = useActiveLeague();
    const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';
    const [searchParams] = useSearchParams();
    const leagueIdParam = searchParams.get('leagueId');
    const activeLeagueId = activeLeague?._id ?? activeLeague?.id ?? null;
    const activeLeagueSlug = activeLeague?.slug ?? null;
    const derivedLeagueId = React.useMemo(() => {
        if (!routeSlug) {
            return leagueIdParam || activeLeagueId || null;
        }

        if (activeLeagueSlug === routeSlug) {
            return activeLeagueId || null;
        }

        return null;
    }, [activeLeagueId, activeLeagueSlug, leagueIdParam, routeSlug]);
    const {
        data: derivedSeasonData
    } = useGetCurrentSeason(derivedLeagueId);
    const derivedSeason = derivedSeasonData?.season ?? null;
    const activeLeaguePrefix = useLeaguePrefix();
    const routePrefix = React.useMemo(() => (
        routeSlug ? `/${routeSlug}` : activeLeaguePrefix
    ), [activeLeaguePrefix, routeSlug]);
    const { user } = useAuth();
    const userId = user?._id ?? user?.id ?? null;
    const currentTeamId = useCurrentTeamId();
    // Removed navigate redirect after bid submission
    const location = useLocation();
    const navigationType = useNavigationType();

    useEffect(() => {
      console.log('PlayerPage History Debug:');
      console.log('  Current React Router location:', location);
      console.log('  Navigation type to this page:', navigationType);
      console.log('  window.history.length:', window.history.length);
      console.log('  window.history.state:', window.history.state);
      console.log('  document.referrer:', document.referrer);
      console.log('  window.location.origin:', window.location.origin);
      console.log('  Back button will work:', document.referrer && document.referrer.includes(window.location.origin) ? 'YES (go back)' : 'NO (use fallback)');
    }, [location, navigationType]);

    const [popOpen, setPopOpen] = React.useState(false);
    const [dropPopOpen, setDropPopOpen] = React.useState(false);
    const [newOffer, setNewOffer] = React.useState([]);
    const queryClient = useQueryClient();
    const ownershipContextKey = React.useMemo(() => {
        if (!wyId || !derivedLeagueId) return null;
        return `${derivedLeagueId}:${wyId}`;
    }, [derivedLeagueId, wyId]);
    const [ownershipState, setOwnershipState] = React.useState({
        key: null,
        currentTeam: null,
        userHasBid: false,
        loading: true
    });
    const ownershipStateMatchesContext = Boolean(
        ownershipContextKey && ownershipState.key === ownershipContextKey
    );
    const currentTeam = ownershipStateMatchesContext ? ownershipState.currentTeam : null;
    const userHasBid = ownershipStateMatchesContext ? ownershipState.userHasBid : false;
    const loadingTeamInfo = Boolean(ownershipContextKey) && (
        !ownershipStateMatchesContext || ownershipState.loading
    );
    
    // Fetch player data
    const { 
        data: playerData, 
        isLoading: isPlayerLoading, 
        error: playerError 
    } = useGetPlayerByWyId(wyId, derivedLeagueId);
    
    const playerIdForSubQueries = wyId;

    // Fetch cumulative season stats for the current league/season
    const {
        data: seasonStatsResponse
    } = useGetPlayerSeasonStats(wyId, derivedLeagueId, derivedSeason, { enabled: !!derivedLeagueId && derivedSeason != null });

    // Extract stats object (API returns { success: true, data: {...} })
    const seasonStats = seasonStatsResponse?.data || null;
    
    // Fetch attributes
    const { 
        data: attributesData, 
        isLoading: isAttributesLoading, 
        error: attributesError 
    } = useGetPlayerAttributes(playerIdForSubQueries);
    
    // Fetch transfer history AND market data from same endpoint
    const { 
        data: marketAndTransferData, 
        isLoading: isTransfersLoading, 
        error: transfersError,
        refetch: refetchMarketData
    } = useGetPlayerTransferHistory(playerIdForSubQueries, derivedLeagueId);
    
    // Extract market data and transfer history from the combined response
    const playerMarketData = React.useMemo(() => {
        if (!marketAndTransferData) return null;
        // The API returns the full market data object
        return marketAndTransferData;
    }, [marketAndTransferData]);
    
    const transferHistory = React.useMemo(() => {
        return marketAndTransferData?.transferHistory || [];
    }, [marketAndTransferData]);
    
    // Extract auction info if present
    const auctionInfo = playerMarketData?.auction || null;
    const currentPrice = Number.isFinite(playerMarketData?.currentPrice)
        ? playerMarketData.currentPrice
        : auctionInfo?.currentPrice ?? 0;
    const releaseRefund = Number(playerMarketData?.dropRefund);
    
    React.useEffect(() => {
        setPopOpen(false);
        setDropPopOpen(false);
        setNewOffer([]);
    }, [ownershipContextKey]);

    // Fetch current team and check if user has made a bid
    React.useEffect(() => {
        let cancelled = false;

        if (!ownershipContextKey || !derivedLeagueId) {
            if (wyId) {
                console.warn('[PlayerPage] Missing derivedLeagueId - skipping ownership resolution to prevent cross-league data leakage');
            }
            setOwnershipState({
                key: null,
                currentTeam: null,
                userHasBid: false,
                loading: false
            });
            return undefined;
        }

        if (!playerMarketData) {
            setOwnershipState({
                key: ownershipContextKey,
                currentTeam: null,
                userHasBid: false,
                loading: true
            });
            return undefined;
        }

        let nextCurrentTeam = null;
        if (playerMarketData.currentTeam) {
            console.log(`[DEBUG] Using team info from market data:`, playerMarketData.currentTeam);
            nextCurrentTeam = playerMarketData.currentTeam;
        } else if (playerMarketData.isFreeAgent) {
            console.log(`[DEBUG] Player is a free agent according to market data`);
        } else {
            console.warn('[PlayerPage] Unexpected backend response: market data loaded without currentTeam or isFreeAgent flag. This may indicate backend payload issues.');
        }

        if (!userId) {
            setOwnershipState({
                key: ownershipContextKey,
                currentTeam: nextCurrentTeam,
                userHasBid: false,
                loading: false
            });
            return undefined;
        }

        setOwnershipState({
            key: ownershipContextKey,
            currentTeam: nextCurrentTeam,
            userHasBid: false,
            loading: true
        });

        const fetchBidInfo = async () => {
            try {
                const bidsResponse = await TransferService.getBids(derivedLeagueId);
                console.log('[DEBUG] Bids response structure:', bidsResponse);

                const bidsData = bidsResponse?.data ?? bidsResponse ?? {};
                const userBids = bidsData.outgoing || [];
                console.log('[DEBUG] User bids:', userBids);

                const existingBid = userBids.find(bid => isActiveTransferBidForPlayer(bid, wyId));
                if (!cancelled) {
                    setOwnershipState({
                        key: ownershipContextKey,
                        currentTeam: nextCurrentTeam,
                        userHasBid: !!existingBid,
                        loading: false
                    });
                }
            } catch (error) {
                console.error('Error fetching team/bid info:', error);
                if (!cancelled) {
                    setOwnershipState({
                        key: ownershipContextKey,
                        currentTeam: nextCurrentTeam,
                        userHasBid: false,
                        loading: false
                    });
                }
            }
        };

        fetchBidInfo();

        return () => {
            cancelled = true;
        };
    }, [wyId, userId, playerMarketData, derivedLeagueId, ownershipContextKey]);
    
    // Enhanced data validation and consistency checks
    const isLoading = isPlayerLoading;
    const error = playerError; // Market data errors are handled separately
    
    // Safe data extraction with validation
    const playerDetails = playerData?.data || {};
    const cPlayer = playerDetails.profile || {};
    const stats = playerDetails.attributes?.attributes || {};
    const development = playerDetails.development || null;
    
    // Validate required player data
    const hasValidPlayer = cPlayer && cPlayer.wyId && Object.keys(cPlayer).length > 0;
    const hasValidStats = stats && Object.keys(stats).length > 0;
    
    // Market data validation (separate from main error handling)
    const hasMarketData = playerMarketData && !error;
    
    console.log('PlayerPage Data Validation:');
    console.log('- wyId:', wyId);
    console.log('- hasValidPlayer:', hasValidPlayer);
    console.log('- hasValidStats:', hasValidStats);
    console.log('- hasMarketData:', hasMarketData);
    console.log('- playerData:', playerData);
    console.log('- error:', error);

    // *** Add Logging Here ***
    console.log('PlayerPage Render - wyId:', wyId);
    console.log('PlayerPage Render - isPlayerLoading:', isPlayerLoading);
    console.log('PlayerPage Render - playerError:', playerError);
    console.log('PlayerPage Render - playerData:', playerData);
    console.log('PlayerPage Render - playerDetails:', playerDetails);
    console.log('PlayerPage Render - cPlayer (profile):', cPlayer);
    console.log('PlayerPage Render - playerMarketData:', playerMarketData);
    // *** End Logging ***

    const closePopup = () => {
        setPopOpen(false);
    }

    const shortlistOptions = [
        { id: 1, title: 'SHORTLIST NAME 1' },
        { id: 2, title: 'SHORTLIST NAME 2' }
    ];
    const [selectedShortlistId, setSelectedShortlistId] = useState(null);
    const handleSelect = (id) => {
        setSelectedShortlistId(id);
        // здесь можно делать что-то ещё, например, добавлять в шортлист
        console.log('Выбран шортлист с ID:', id);
    };

    const NewOfferPopup = ({ newOffer, popOpen, closePopup, currentPrice, auctionId }) => {
        const queryClient = useQueryClient();

        // Determine minimum bid based on player status
        const isFreeAgent = playerMarketData?.isFreeAgent;
        const marketValue = playerMarketData?.marketValue ?? 0;
        const serverMin = Number.isFinite(playerMarketData?.nextMinimumBid) ? playerMarketData.nextMinimumBid : null;
        const derivedMin = computeNextMinimumBid({
            isFreeAgent,
            marketValue,
            currentPrice
        });
        // Quick Play locks the bid to the player's exact transfer value.
        const quickplayFixedBid = (isQuickplayLeague && Number.isFinite(marketValue) && marketValue > 0)
            ? marketValue
            : null;
        const quickplayValueMissing = isQuickplayLeague && quickplayFixedBid === null;
        const minBid = quickplayFixedBid ?? Math.max(derivedMin, serverMin ?? 0);

        const formatNumber = (n) => n.toLocaleString('en-US');
        const initialAmount = React.useMemo(
            () => quickplayFixedBid ?? Math.max(minBid, currentPrice + BID_STEP),
            [quickplayFixedBid, minBid, currentPrice]
        );

        const [bidAmount, setBidAmount] = React.useState(initialAmount);
        const [displayVal, setDisplayVal] = React.useState(formatNumber(initialAmount));
        const [bidError, setBidError] = React.useState(null);
        const [submitting, setSubmitting] = React.useState(false);

        // Remaining budget so impossible offers are blocked before they reach
        // the server. An unknown budget must not block bidding; the server
        // still validates every bid.
        const [remainingBudget, setRemainingBudget] = React.useState(null);
        React.useEffect(() => {
            if (!popOpen || !derivedLeagueId) return undefined;
            let cancelled = false;
            TransferService.getTeamInfo(derivedLeagueId)
                .then((response) => {
                    const budget = response?.data?.remainingBudget;
                    if (!cancelled && Number.isFinite(budget)) {
                        setRemainingBudget(budget);
                    }
                })
                .catch((err) => {
                    console.error('Error fetching remaining budget:', err);
                });
            return () => { cancelled = true; };
        }, [popOpen, derivedLeagueId]);

        const effectiveOffer = quickplayFixedBid ?? bidAmount;
        const insufficientBudget =
            Number.isFinite(remainingBudget) && effectiveOffer > remainingBudget;

        // Reset amount each time the popup opens or the current price changes
        React.useEffect(() => {
            if (!popOpen || newOffer.length) return;
            const initial = quickplayFixedBid ?? Math.max(minBid, currentPrice + BID_STEP);
            setBidAmount(initial);
            setDisplayVal(formatNumber(initial));
            setBidError(null);
        }, [popOpen, currentPrice, minBid, newOffer.length, quickplayFixedBid]);

        const sendOffer = async () => {
            if (quickplayValueMissing || insufficientBudget) {
                return;
            }

            // In Quick Play the submitted amount is always the player's transfer value.
            const amountToSubmit = quickplayFixedBid ?? bidAmount;

            if (amountToSubmit < minBid) {
                setBidError(`Minimum allowed €${formatNumber(minBid)}`);
                return;
            }

            // Ensure we have a leagueId to submit with
            if (!derivedLeagueId) {
                toast.error('League information not available. Please try again.');
                return;
            }
            
            setSubmitting(true);
            try {
                const bidData = {
                    playerId: parseInt(wyId),
                    amount: amountToSubmit,
                    contractOffer: {
                        length: 3,
                        salary: Math.round(amountToSubmit / 10000) // rough calc
                    }
                };
                const response = await TransferService.submitBid(bidData, derivedLeagueId);
                // TransferService returns the parsed JSON payload: { message, bid }
                if (response && response.bid) {
                    setNewOffer([{ offer: amountToSubmit, submitted: true, bidId: response.bid._id }]);
                    toast.success(`Bid of €${amountToSubmit.toLocaleString()} submitted successfully!`);
                    closePopup(); // Close the popup after successful submission
                    // Invalidate market data cache to fetch latest currentPrice across tabs
                    queryClient.invalidateQueries({ queryKey: ["player-market-data", wyId] });
                }
            } catch (error) {
                console.error('Error submitting bid:', error);
                toast.error('Failed to submit bid: ' + (error.response?.data?.error || error.message));
            } finally {
                setSubmitting(false);
            }
        };

        if (newOffer.length) {
            return (
                <Popup popOpen={popOpen} close={closePopup}>
                    <h3>
                        MAKE OFFER
                    </h3>

                    <div className="offer-description">
                        Several users have made offers for this player. You will be notified of the transfer's status
                        within {isQuickplayLeague ? '5 minutes' : '1 hour'}.
                    </div>

                    <div className="thin-block">
                        <button
                            type="submit"
                            className="submit-btn submit_offer_btn"
                            onClick={() => {closePopup()}}
                        >
                            CLOSE
                        </button>
                    </div>

                </Popup>
            );
        }

        return (
            <Popup popOpen={popOpen} close={closePopup}>
                <h3>MAKE OFFER</h3>

                <div className="player-ownership" style={{ justifyContent: 'center', marginTop: 0, marginBottom: '16px' }}>
                    {!isFreeAgent && currentTeam?.name ? (
                        <>
                            <span className="ownership-prefix">Owned by</span>
                            <span className="ownership-status owned">{currentTeam.name}</span>
                        </>
                    ) : (
                        <span className="ownership-status free-agent">Free Agent</span>
                    )}
                </div>

                <div className="form-block">
                    <h4>ENTER AMOUNT</h4>

                    <div className="offer-grid">
                        <div className="col">
                            <div className="currency-input" style={{ position: 'relative', display: 'inline-block' }}>
                                <span className="currency-symbol" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>€</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9,]*"
                                    id="bidAmount"
                                    value={quickplayFixedBid != null ? formatNumber(quickplayFixedBid) : displayVal}
                                    readOnly={isQuickplayLeague}
                                    style={{ paddingLeft: '28px' }}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                        const num = parseInt(raw, 10);
                                        if (isNaN(num)) {
                                            setDisplayVal('');
                                            setBidAmount(0);
                                            setBidError(`Minimum allowed €${formatNumber(minBid)}`);
                                            return;
                                        }
                                        setBidAmount(num);
                                        setDisplayVal(formatNumber(num));
                                        if (num >= minBid) {
                                            setBidError(null);
                                        } else {
                                            setBidError(`Minimum allowed €${formatNumber(minBid)}`);
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="col">
                            <button
                                type="submit"
                                className="submit-btn submit_offer_btn"
                                onClick={sendOffer}
                                disabled={submitting || quickplayValueMissing || bidAmount < minBid || insufficientBudget}
                            >
                                {submitting ? 'SUBMITTING...' : 'SUBMIT'}
                            </button>
                        </div>
                    </div>
                </div>
                {isQuickplayLeague && (
                    <p className={quickplayValueMissing ? "error-text" : "helper-text"}>
                        {quickplayValueMissing
                            ? "This player cannot be signed right now because no transfer value is available."
                            : "Quick Play bids are fixed at the player's transfer value."}
                    </p>
                )}
                {insufficientBudget && (
                    <p className="error-text">
                        You don't have enough remaining budget for this bid.
                    </p>
                )}
                {!isQuickplayLeague && (
                <p className={bidError ? "error-text" : "helper-text"}>
                    {bidError ?? `Minimum allowed €${formatNumber(minBid)}`}
                    {currentPrice > 0 && !bidError && (
                        <span> · Current highest bid €{formatNumber(currentPrice)}</span>
                    )}
                </p>
                )}
            </Popup>
        );
    }

    // Handle loading state
    if (isLoading) {
      console.log('PlayerPage: Rendering Loading State');
        return (
            <div className="page-player">
                <div className="wrapper">
                    <div className="top-block">
                        <div className="breadcrumbs">
                            <Link to={`${routePrefix}/`}>League</Link>
                            / <Link to={`${routePrefix}/`}>MY TEAM</Link>
                            / <span>Loading...</span>
                        </div>

                        <BackButton>
                            <span className="ico icon-back" />
                            BACK
                        </BackButton>
                    </div>
                    <div className="loading-container">
                        <Spinner />
                        <p>Loading player data...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Handle error state
    if (error) {
      console.log('PlayerPage: Rendering Error State', error);
        return (
            <div className="page-player">
                <div className="wrapper">
                    <div className="top-block">
                        <div className="breadcrumbs">
                            <Link to={`${routePrefix}/`}>League</Link>
                            / <Link to={`${routePrefix}/`}>MY TEAM</Link>
                            / <span>Error</span>
                        </div>

                        <BackButton>
                            <span className="ico icon-back" />
                            BACK
                        </BackButton>
                    </div>
                    <ErrorMessage message={error.message || 'Failed to load player data'} />
                </div>
            </div>
        );
    }

    // Handle empty state - use improved validation
    if (!hasValidPlayer) {
      console.log('PlayerPage: Rendering Empty State (Player Not Found)');
        return (
            <div className="page-player">
                <div className="wrapper">
                    <div className="top-block">
                        <div className="breadcrumbs">
                            <Link to={`${routePrefix}/`}>League</Link>
                            / <Link to={`${routePrefix}/`}>MY TEAM</Link>
                            / <span>Player Not Found</span>
                        </div>

                        <BackButton>
                            <span className="ico icon-back" />
                            BACK
                        </BackButton>
                    </div>
                    <div className="empty-state">
                        <h2>Player Not Found</h2>
                        <p>The player you are looking for does not exist or has been removed.</p>
                        {!hasValidStats && (
                          <p><small>Player attributes are also unavailable.</small></p>
                        )}
                        {error && (
                          <p><small>Market data: {error}</small></p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Extract player details for rendering
    const playerName = cPlayer.shortName || 'Unknown'; // Use shortName from profile
    const playerAge = resolvePlayerAge(cPlayer, 'N/A');
    const playerNationality = cPlayer.passportArea?.name || 'Unknown';
    const playerNationalityCode = cPlayer.passportArea?.alpha2code || null;
    const playerPosition = cPlayer.role?.code3 || 'Unknown'; // Use same format as transfer page (role.code3)
    const playerTeam = currentTeam?.name || 'Free Agent'; // Use fetched team name
    const playerTeamId = currentTeam?._id || currentTeam?.id || null; // Accept both _id and id fields
    const playerOverallTier = tierOf(playerDetails.overall);

    // Determine Role for PlayerStats - Updated to check for GKP (database uses GKP for goalkeepers)
    const playerRole = (playerPosition === 'GKP') ? 'GK' : 'Outfield'; // Updated to use code3 format
    
    // Debug logging for position detection
    console.log('PlayerPage Role Detection:', {
        playerPositionCode3: playerPosition,
        determinedRole: playerRole,
        isGoalkeeper: playerPosition === 'GKP'
    });

    // Enhanced market value display with better error handling
    const renderPlayerValue = () => {
        // Handle market data fetch errors
        if (error) {
            return (
                <span className="value-unavailable">
                    Value unavailable
                </span>
            );
        }
        
        // Handle missing market data entirely
        if (!playerMarketData) {
            return (
                <span className="value-unavailable">
                    Value not available
                </span>
            );
        }
        
        // Handle null or missing market values
        if (!playerMarketData.marketValue && playerMarketData.marketValue !== 0) {
            return (
                <span className="value-unavailable">
                    Value not available
                </span>
            );
        }
        
        // Handle retired players
        if (playerMarketData.status === 'retired') {
            return (
                <span className="value-retired">
                    Retired
                </span>
            );
        }
        
        // Display verified database values with proper formatting and blue color
        const marketValue = playerMarketData.marketValue;
        let formattedValue;
        
        if (marketValue >= 1000000) {
            formattedValue = `€${(marketValue / 1000000).toFixed(1)}M`;
        } else if (marketValue >= 1000) {
            formattedValue = `€${Math.round(marketValue / 1000)}k`;
        } else {
            formattedValue = `€${marketValue}`;
        }
        
        return (
            <span className="value-verified" style={{ color: '#007bff' }}>
                {formattedValue}
            </span>
        );
    };

    // Backend now guarantees numeric season stats, but keep a safe fallback to 0.
    // Helper to display '-' when a stat is missing
    const fmtStat = (val) => (val === undefined || val === null ? '-' : val);

    const games = fmtStat(seasonStats?.games ?? cPlayer.games);
    const goals = fmtStat(seasonStats?.goals ?? cPlayer.goals);
    const assists = fmtStat(seasonStats?.assists ?? cPlayer.assists);
    const keyTackles = fmtStat(seasonStats?.tackles ?? cPlayer.tackles);
    const keyPasses = fmtStat(seasonStats?.passesCompleted ?? cPlayer.passesCompleted);
    const keyShots = fmtStat(seasonStats?.shotsOnTarget ?? cPlayer.shotsOnTarget);
    const yellow = fmtStat(seasonStats?.yellowCards ?? seasonStats?.yellow ?? cPlayer.yellow);
    const red = fmtStat(seasonStats?.redCards ?? seasonStats?.red ?? cPlayer.red);
    const points = fmtStat(seasonStats?.points ?? cPlayer.points);
    const pointsChange = cPlayer.pointsChange || 'semi';

    // Determine button text and state
    const getButtonState = () => {
        if (!ownershipContextKey || !userId || transfersError) return { text: 'Make Offer', disabled: true };
        if (!playerMarketData) return { text: 'Loading...', disabled: true };
        if (loadingTeamInfo) return { text: 'Loading...', disabled: true };
        if (userHasBid) return { text: 'Offer Made', disabled: true };
        return { text: 'Make Offer', disabled: false };
    };

    const buttonState = getButtonState();

    // Determine if the player is owned by current user in the active league
    const isOwnedByUser = !!(
        derivedLeagueId &&
        ownershipStateMatchesContext &&
        !loadingTeamInfo &&
        currentTeamId &&
        currentTeam &&
        (currentTeam._id === currentTeamId || currentTeam.id === currentTeamId)
    );

    const handleConfirmDropOnProfile = async () => {
        if (!derivedLeagueId) {
            toast.error('League information not available. Please try again.');
            setDropPopOpen(false);
            return;
        }
        if (!isOwnedByUser || !currentTeamId) return;
        try {
            await TeamService.dropPlayer(currentTeamId, parseInt(wyId), derivedLeagueId);
            toast.success('Player released successfully');
            // Update local ownership immediately
            setOwnershipState(prev => (
                prev.key === ownershipContextKey
                    ? { ...prev, currentTeam: null }
                    : prev
            ));
            // Invalidate related caches
            queryClient.invalidateQueries({ queryKey: ['player-market-data', wyId] });
            queryClient.invalidateQueries({ queryKey: ['player', wyId] });
            if (currentTeamId && derivedLeagueId && derivedSeason != null) {
                queryClient.invalidateQueries({ queryKey: ['team-players', currentTeamId, derivedLeagueId, derivedSeason] });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to release player';
            toast.error(errorMessage);
        } finally {
            setDropPopOpen(false);
        }
    };

    console.log('PlayerPage: Rendering Player Details');

    return (
        <div className="page-player">

            <div className="wrapper">
                <div className="top-block">
                    {/*<div className="breadcrumbs">*/}
                    {/*    <Link to={`${routePrefix}/`}>League</Link>*/}
                    {/*    / <Link to={`${routePrefix}/`}>MY TEAM</Link>*/}
                    {/*    / <span>{playerName}</span>*/}
                    {/*</div>*/}

                    <BackButton>
                        <span className="ico icon-back" />
                        BACK
                    </BackButton>

                    <div class="top-ctrls-container">
                        {/*<TeamDropdown*/}
                        {/*    props={shortlistOptions}*/}
                        {/*    selectedId={selectedShortlistId}   // или activeItemId – без разницы*/}
                        {/*    setId={handleSelect}*/}
                        {/*    startVal="ADD TO SHORTLIST"*/}
                        {/*    showPlaceholder={true}*/}
                        {/*/>*/}

                        <div className="btn-holder">
                            {isOwnedByUser ? (
                                <button
                                    type="button"
                                    className="b-button offer_btn"
                                    onClick={() => setDropPopOpen(true)}
                                    aria-label="Release Player"
                                    style={{ backgroundColor: '#dc3545', border: '1px solid #dc3545' }}
                                >
                                    {Number.isFinite(releaseRefund) && releaseRefund > 0
                                        ? `RELEASE FOR ${numToPrice(releaseRefund).toUpperCase()}`
                                        : 'RELEASE'}
                                </button>
                            ) : (
                                <div>
                                    <TutorialTooltip
                                        id="player-offer-btn-tooltip"
                                        title="Transfer offers"
                                        description="If a player has been signed by another team, you can still make an offer for this player. It’s then up to that team’s manager to accept, reject or counter your offer."
                                        className="f-right"
                                    />

                                    <button
                                        type="button"
                                        className={`b-button offer_btn ${userHasBid ? 'bid-made' : ''}`}
                                        onClick={async () => {
                                            if (!derivedLeagueId || !playerMarketData || transfersError || loadingTeamInfo) {
                                                toast.error('League information not available. Please try again.');
                                                return;
                                            }
                                            try {
                                                const refreshResult = await refetchMarketData(); // get fresh auction price
                                                if (refreshResult?.error) {
                                                    throw refreshResult.error;
                                                }
                                                setPopOpen(true);
                                            } catch (err) {
                                                console.error('Failed to refresh market data before popup:', err);
                                                toast.error('Could not refresh transfer information. Please try again.');
                                            }
                                        }}
                                        disabled={buttonState.disabled}
                                        style={{
                                            ...(userHasBid && {
                                                backgroundColor: '#6c757d',
                                                color: '#adb5bd',
                                                cursor: 'not-allowed',
                                                opacity: '0.6'
                                            })
                                        }}
                                        aria-label="Make Offer"
                                    >
                                        {buttonState.text}
                                    </button>

                                </div>
                            )}
                        </div>
                    </div>


                </div>


                <div className="player-info-grid">
                    <div className="player-info-block">
                        <div className="title-block">
                            <div className="flag">
                                {playerNationalityCode ? (
                                    <Flag code={playerNationalityCode} fallback={playerNationality} />
                                ) : (
                                    <span>{playerNationality}</span>
                                )}
                            </div>
                            <h1>
                                {playerName}
                            </h1>
                            {playerDetails.overall != null && (
                                <div className={`pcard-ovr-chip pcard-ovr-chip--${playerOverallTier || 'bronze'}`}>
                                    <strong className="pcard-ovr-chip__value">{playerDetails.overall}</strong>
                                    <span className="pcard-ovr-chip__label">OVERALL</span>
                                </div>
                            )}


                        </div>

                        <div className="info-block">
                            <div className="info">
                                Age: <span>{playerAge}</span>
                            </div>
                            <div className="info">
                                Nationality: <span>{playerNationality}</span>
                            </div>
                            <div className="info">
                                Position: <span>{playerPosition}</span>
                            </div>
                            <div className="info">
                                Value: <span>{renderPlayerValue()}</span>
                            </div>
                            <div className="info">
                                Team: {playerTeamId ? <Link to={playerTeamId === currentTeamId ? buildTeamLink(playerTeamId, routePrefix) : buildPublicTeamLink(playerTeamId, routePrefix)}>{playerTeam}</Link> : <span>{playerTeam}</span>}
                            </div>
                        </div>

                    </div>

                    <div className="attributes-block">
                        <h4>
                            Attributes
                        </h4>

                        <div className="stats-grid">
                            <PlayerStats props={stats} role={playerRole} />
                        </div>
                    </div>

                    <div className="chart-block">
                        <h4>
                            {playerRole === 'GK' ? 'Attributes' : 'Radar'}
                        </h4>

                        <div className="chart">
                            <TutorialTooltip
                                id="player-radar-tooltip"
                                title="Player attributes"
                                description="All field players have 9 attributes, each corresponding to a certain skill. When a league is created, player attributes correspond to their real world performance and may refresh depending on how they perform in real life with each new league you play in. After that, their development is up to you."
                                className="f-right"
                            />

                            <PlayerChart props={stats} role={playerRole} />
                        </div>


                    </div>

                    <PlayerDevelopmentPanel development={development} />

                    <div className="stats-block">
                        <div className="title-block">
                            <h4>
                                Stats
                            </h4>

                            {/*<TeamDropdown props={seasons} />*/}

                        </div>

                        <div className="stats-row">
                            <div className="col">
                                <h5 className="title">
                                    Games
                                </h5>
                                <div className="value">
                                    {games}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Goals
                                </h5>
                                <div className="value">
                                    {goals}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Assists
                                </h5>
                                <div className="value">
                                    {assists}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Key Tackles
                                </h5>
                                <div className="value">
                                    {keyTackles}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Key Passes
                                </h5>
                                <div className="value">
                                    {keyPasses}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Key Shots
                                </h5>
                                <div className="value">
                                    {keyShots}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Yellow
                                </h5>
                                <div className="value">
                                    {yellow}
                                </div>
                            </div>
                            <div className="col">
                                <h5 className="title">
                                    Red
                                </h5>
                                <div className="value">
                                    {red}
                                </div>
                            </div>
                            {/*<div className="col">*/}
                            {/*    <h5 className="title">*/}
                            {/*        Points*/}
                            {/*    </h5>*/}
                            {/*    <div className="value">*/}
                            {/*        <div className="points">*/}
                            {/*            {points}*/}
                            {/*            <span className={`ico ${pointsChange}`} />*/}
                            {/*        </div>*/}
                            {/*    </div>*/}
                            {/*</div>*/}

                        </div>

                    </div>



                    {/* Pass transfer history data, loading, and error state */}
                    <TransferBlock 
                        transfers={transferHistory || []} 
                        isLoading={isTransfersLoading} 
                        error={transfersError} 
                        playerWyId={wyId}
                        leagueId={derivedLeagueId}
                    />

                </div>


            </div>

            {popOpen && !isOwnedByUser && (
                <NewOfferPopup
                    newOffer={newOffer}
                    popOpen={popOpen}
                    closePopup={() => setPopOpen(false)}
                    currentPrice={currentPrice}
                    auctionId={auctionInfo?.auctionId}
                />
            )}

            {isOwnedByUser && (
                <Popup popOpen={dropPopOpen} close={() => setDropPopOpen(false)}>
                    <h3>CONFIRM RELEASE</h3>
                    <div className="offer-description text-center" style={{ marginBottom: '20px' }}>
                        {Number.isFinite(releaseRefund) && releaseRefund > 0
                            ? `Are you sure you want to release this player? You will get ${numToPrice(releaseRefund)} back in transfer value. This action cannot be undone.`
                            : 'Are you sure you want to release this player? This action cannot be undone.'}
                    </div>
                    <div className="buttons-block">
                        <button
                            type="button"
                            className="submit-btn trans-btn white"
                            onClick={() => setDropPopOpen(false)}
                        >
                            CANCEL
                        </button>
                        <button
                            type="button"
                            className="submit-btn"
                            onClick={handleConfirmDropOnProfile}
                        >
                            CONFIRM RELEASE
                        </button>
                    </div>
                </Popup>
            )}

        </div>
    )
}

export {PlayerPage}
